/**
 * SharedNavBridge — Mounts the LEARNature-ly shared header + footer inside
 * the AWENI shell so the global navigation menu (Home, Read, Play, Apps) is
 * present everywhere. Single source of truth for cross-platform nav.
 *
 * The shared scripts are loaded with `defer` from index.html and expose
 * `window.LnNav` / `window.LnFooter`. We poll briefly because module bootstrap
 * can race the deferred script depending on cache state.
 */

import type { AweniContext } from '../context/AweniContext';

interface LnNavApi {
  init: (opts: {
    lang?: string;
    logoUrl?: string;
    logoText?: string;
    i18nBase?: string;
  }) => Promise<void>;
  setLang: (lang: string) => void;
  setUser: (user: unknown) => void;
}

interface LnFooterApi {
  init: (opts?: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    LnNav?: LnNavApi;
    LnFooter?: LnFooterApi;
  }
}

const LN_NAV_HEIGHT_PX = 60;

/**
 * Wait for a global to be defined on window, polling at a small interval.
 * Returns null after the timeout (caller should degrade gracefully).
 */
function waitForGlobal<K extends keyof Window>(
  name: K,
  timeoutMs = 4000,
): Promise<NonNullable<Window[K]> | null> {
  return new Promise((resolve) => {
    const w = window as unknown as Record<string, unknown>;
    if (w[name as string]) {
      resolve(w[name as string] as NonNullable<Window[K]>);
      return;
    }
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (w[name as string]) {
        window.clearInterval(timer);
        resolve(w[name as string] as NonNullable<Window[K]>);
      } else if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer);
        resolve(null);
      }
    }, 50);
  });
}

/** Mount LN shared nav at body.firstChild (its init() places itself there). */
export async function mountSharedHeader(ctx: AweniContext): Promise<void> {
  const lnNav = await waitForGlobal('LnNav');
  if (!lnNav) {
    console.warn('[SharedNavBridge] LnNav not loaded — shared header skipped');
    return;
  }

  try {
    await lnNav.init({
      lang: ctx.i18n.locale,
      logoUrl: '/',
      logoText: 'LEARNature-ly',
    });
  } catch (e) {
    console.warn('[SharedNavBridge] LnNav.init failed:', e);
    return;
  }

  // Bridge AWENI locale changes into LnNav so the menu translates in sync.
  const i18n = ctx.i18n as typeof ctx.i18n & {
    onChange?: (cb: (locale: string) => void) => void;
  };
  i18n.onChange?.((locale) => lnNav.setLang(locale));

  // Layout strategy (2026-04-18 v2):
  // - LN nav is an overlay pinned at top:0 (doesn't reshape #app).
  // - AWENI's fixed topbar slides below the LN nav (top:60).
  // - Module viewport padding-top grows to reserve space for both.
  // - #app remains 100vh (unchanged) so AWENI's fixed bottomnav at bottom:0
  //   lands at the actual viewport bottom and stays clickable.
  document.documentElement.style.setProperty(
    '--ln-nav-height',
    `${LN_NAV_HEIGHT_PX}px`,
  );

  const lnRoot = document.getElementById('ln-nav-root');
  if (lnRoot) {
    lnRoot.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'right:0',
      `height:${LN_NAV_HEIGHT_PX}px`,
      'z-index:1000',
    ].join(';');
  }

  const tb = document.getElementById('aweni-topbar');
  if (tb) tb.style.top = `${LN_NAV_HEIGHT_PX}px`;

  const viewport = document.querySelector<HTMLElement>('.module-viewport');
  if (viewport) {
    // Existing inline: padding-top:48px. Add LN nav height so content starts
    // below both bars.
    viewport.style.paddingTop = `${48 + LN_NAV_HEIGHT_PX}px`;
  }
}

/** Mount LN shared footer. The script appends to body, no host needed. */
export async function mountSharedFooter(): Promise<void> {
  const lnFooter = await waitForGlobal('LnFooter');
  if (!lnFooter) {
    console.warn('[SharedNavBridge] LnFooter not loaded — shared footer skipped');
    return;
  }
  try {
    lnFooter.init();
  } catch (e) {
    console.warn('[SharedNavBridge] LnFooter.init failed:', e);
  }
}

/**
 * Map an AWENI module to the canonical standalone URL. AWENI hash routes
 * that mirror standalone apps (#/games, #/blog, #/memoriz, #/balancetab,
 * #/selecto) declare those standalone URLs as canonical so search engines
 * see one URL per piece of content.
 */
const MODULE_CANONICAL: Record<string, string> = {
  games: 'https://learnature-ly.com/games/',
  blog: 'https://learnature-ly.com/blog/',
  memoriz: 'https://learnature-ly.com/memoriz/',
  balancetab: 'https://learnature-ly.com/balancetab/',
  selecto: 'https://learnature-ly.com/selecto/',
  selectow: 'https://learnature-ly.com/selectow/',
  selectos: 'https://learnature-ly.com/selectow/',
};

const AWENI_BASE = 'https://learnature-ly.com/aweni/';

/** Update the page <link rel="canonical"> based on the current AWENI route. */
export function updateCanonical(moduleId: string, _subpath: string): void {
  const link = document.getElementById('ln-canonical') as HTMLLinkElement | null;
  if (!link) return;
  const canonical = MODULE_CANONICAL[moduleId] ?? `${AWENI_BASE}#/${moduleId}`;
  if (link.href !== canonical) link.href = canonical;
}
